import { ComponentProps, useEffect, useState } from "react"
import {
	Badge,
	Box,
	Button,
	Divider,
	Icons,
	Inline,
	Money,
	Select,
	Stack,
	Table,
	Text,
	Time,
	Tooltip,
} from "@sembark-travel/ui-base"
import {
	dateToQuery,
	formatDate,
	getDiff,
	localOrUtcTimestampToLocalDate,
	parseDateFromQuery,
} from "@sembark-travel/datetime-utils"
import {
	CheckboxInputField,
	DatePickerField,
	SelectField,
} from "@sembark-travel/ui-form"
import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { IHotel, SelectHotels } from "../Hotels"
import { generatePath } from "../router-utils"
import { Batch } from "../shared/Batch"
import { ITripSource, SelectTripSources } from "../TripSources"
import {
	hotelOtaBookingsXHR,
	TOtaBooking,
	TOtaBookingChild,
	TOtaBookingStageState,
} from "./store"

// How the designs show a date with its time, e.g. 18 Oct 2026 • 09:12
const otaDateTimeFormat = "D MMM YYYY [•] HH:mm"

type TStageOption = { id: TOtaBookingStageState; name: string }

const stageOptions: Array<TStageOption> = [
	{ id: "booked", name: "Booked" },
	{ id: "cancelled", name: "Cancelled" },
]

interface IFilters extends TSearchParams {
	hotels?: Array<Pick<IHotel, "id" | "name">>
	trip_sources?: Array<Pick<ITripSource, "id" | "name">>
	stages?: Array<TStageOption>
	stay_after?: Date
	stay_before?: Date
	not_synced?: boolean
}

interface IFiltersInLocation extends TSearchParams {
	hotels?: string[]
	trip_sources?: string[]
	stages?: Array<TOtaBookingStageState>
	sa?: string
	sb?: string
	not_synced?: string
}

function idNamesToQuery(values: Array<{ id: number; name: string }>) {
	return values.map((value) => `${value.id}_${value.name}`)
}

function queryToIdNames(values: string[]) {
	return values.map((value) => {
		const [id, ...name] = value.split("_")
		return { id: Number(id), name: name.join("_") }
	})
}

function filtersToLocationQuery(filters: IFilters): IFiltersInLocation {
	const {
		q,
		page,
		hotels,
		trip_sources,
		stages,
		stay_after,
		stay_before,
		not_synced,
	} = filters
	const query: IFiltersInLocation = {}
	if (q) query.q = q
	if (page) query.page = page
	if (hotels?.length) query.hotels = idNamesToQuery(hotels)
	if (trip_sources?.length) query.trip_sources = idNamesToQuery(trip_sources)
	if (stages?.length) query.stages = stages.map((stage) => stage.id)
	if (stay_after) query.sa = dateToQuery(stay_after)
	if (stay_before) query.sb = dateToQuery(stay_before)
	if (not_synced) query.not_synced = "1"
	return query
}

function locationQueryToFilters(query: IFiltersInLocation): IFilters {
	const { q, page, hotels, trip_sources, stages, sa, sb, not_synced } = query
	const filters: IFilters = {}
	if (q) filters.q = q
	if (page) filters.page = page
	if (hotels?.length) filters.hotels = queryToIdNames(hotels)
	if (trip_sources?.length) filters.trip_sources = queryToIdNames(trip_sources)
	if (stages?.length) {
		filters.stages = stageOptions.filter((option) => stages.includes(option.id))
	}
	if (sa) filters.stay_after = parseDateFromQuery(sa)
	if (sb) filters.stay_before = parseDateFromQuery(sb)
	if (not_synced) filters.not_synced = true
	return filters
}

function filtersToRequestParams(filters: IFilters, hotelId?: number) {
	const {
		hotels = [],
		trip_sources = [],
		stages = [],
		stay_after,
		stay_before,
		not_synced,
		page = 1,
		...otherParams
	} = filters
	return {
		// The list of a hotel ignores any hotels left in the url
		hotels: hotelId ? [hotelId] : hotels.map((hotel) => hotel.id),
		trip_sources: trip_sources.map((tripSource) => tripSource.id),
		stages: stages.map((stage) => stage.id),
		// The API takes the calendar days and matches the stays overlapping them
		stay_from: stay_after ? dateToQuery(stay_after) : undefined,
		stay_to: stay_before ? dateToQuery(stay_before) : undefined,
		not_synced: not_synced ? 1 : undefined,
		// Adds the counts of the chips to the meta of the same response
		include: "warnings",
		page,
		...otherParams,
		limit: 10,
	}
}

export function HotelOtaBookings({ hotelId }: { hotelId?: number } = {}) {
	const [queryFilters, setQueryFilters] = useLocationQuery<
		IFilters,
		IFiltersInLocation
	>({
		toQuery: filtersToLocationQuery,
		fromQuery: locationQueryToFilters,
	})
	const [params, setParams] = useSearch<IFilters>(queryFilters)
	// The chips change filters from outside of the search form, remount it to pick them up
	const [searchKey, setSearchKey] = useState(0)
	const { hasPermission } = useCheckPermissions()
	const canRetry = hasPermission(PERMISSIONS.MODIFY_HOTEL_BOOKINGS)
	// The counts for the chips, they come with the list and follow its filters
	const [warnings, setWarnings] = useState<{ not_synced: number }>()
	useEffect(() => {
		setQueryFilters(params)
	}, [params, setQueryFilters])

	function toggleFilter(filter: "not_synced") {
		setParams({ ...params, [filter]: !params[filter], page: 1 })
		setSearchKey((prev) => prev + 1)
	}

	return (
		<Search
			key={searchKey}
			title="Bookings"
			placeholder="Search by Guest, B2B Source, Booking ID, Confirmation ID"
			Filters={hotelId ? HotelOtaBookingFilters : OtaBookingFilters}
			initialParams={params}
			areAdvancedFiltersApplied={areAdvancedFiltersAppliedDefault}
			resetParams={() => ({ q: "", page: 1 })}
			onSearch={(newParams) => setParams({ ...newParams, page: 1 })}
			actions={
				<Inline gap="4" alignItems="center">
					{warnings?.not_synced || params.not_synced ? (
						<Button
							level="tertiary"
							status="danger"
							size="sm"
							title="Show not synced bookings"
							onClick={() => toggleFilter("not_synced")}
						>
							<Icons.CircleAlert /> {warnings?.not_synced || 0} Not Synced
						</Button>
					) : null}
				</Inline>
			}
		>
			<ListView<TOtaBooking, IFilters>
				pageKey={
					hotelId
						? `/ota-bookings-listing/hotel-${hotelId}`
						: "/ota-bookings-listing"
				}
				params={params}
				fetch={async (xhr, params) => {
					const response = await hotelOtaBookingsXHR(xhr).getOtaBookings(
						filtersToRequestParams(params, hotelId)
					)
					setWarnings(response.meta.warnings)
					return response
				}}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items: bookings, refresh }) => (
					<OtaBookingsTable
						bookings={bookings}
						showHotel={!hotelId}
						canRetry={canRetry}
						onChange={refresh}
					/>
				)}
			</ListView>
		</Search>
	)
}

function OtaBookingFilters() {
	return (
		<Stack gap="4">
			<SelectField label="Hotel" name="hotels" select={SelectHotels} multiple />
			<OtaBookingCommonFilters />
		</Stack>
	)
}

function HotelOtaBookingFilters() {
	return (
		<Stack gap="4">
			<OtaBookingCommonFilters />
		</Stack>
	)
}

function OtaBookingCommonFilters() {
	return (
		<>
			<SelectField
				label="B2B Sources"
				name="trip_sources"
				select={SelectTripSources}
				multiple
				fetchOnMount
			/>
			<SelectField
				label="Stage"
				name="stages"
				select={Select}
				options={stageOptions}
				multiple
			/>
			<Stack>
				<label htmlFor="stay_after">Stay Window</label>
				<Inline gap="2" alignItems="center">
					<DatePickerField name="stay_after" dateFormat="DD MMM'YY" />
					<Icons.SwitchHorizontal />
					<DatePickerField
						name="stay_before"
						rightAlign
						dateFormat="DD MMM'YY"
					/>
				</Inline>
			</Stack>
			<CheckboxInputField name="not_synced" label="Show Not Synced Bookings" />
		</>
	)
}

function OtaBookingsTable({
	bookings,
	showHotel,
	canRetry,
	onChange,
}: {
	bookings: Array<TOtaBooking>
	showHotel: boolean
	canRetry: boolean
	onChange: () => void
}) {
	const headers = [
		"Booking ID",
		"Guest/B2B Source",
		...(showHotel ? ["Hotel"] : []),
		"Stay",
		"Occupancy",
		"Stage",
		"Price",
	]
	return (
		<Table
			bordered
			responsive
			headers={headers}
			alignCols={{ [headers.length - 1]: "right" }}
			rowKeys={bookings.map((booking) => booking.id)}
			rows={bookings.map((booking) => [
				<OtaBookingReference booking={booking} />,
				<OtaBookingGuest booking={booking} />,
				...(showHotel ? [<OtaBookingHotel booking={booking} />] : []),
				<OtaBookingStayDates booking={booking} />,
				<OtaBookingOccupancy booking={booking} />,
				<OtaBookingStageCell
					booking={booking}
					canRetry={canRetry}
					onChange={onChange}
				/>,
				<OtaBookingPrice booking={booking} />,
			])}
		/>
	)
}

function OtaBookingReference({ booking }: { booking: TOtaBooking }) {
	return (
		<Stack gap="1">
			<Inline gap="2" alignItems="center">
				<Link
					to={generatePath("/ota-bookings/:bookingId", {
						bookingId: String(booking.id),
					})}
					color="accent"
					fontWeight="semibold"
					anchored
				>
					#{booking.display_id || booking.id}
				</Link>
				{booking.remote_booking_is_synced ? null : (
					<OtaBookingSyncStatus booking={booking} />
				)}
			</Inline>
			{booking.booking_confirmation_number ? (
				<Inline gap="1" alignItems="center" color="muted" fontSize="sm">
					<Icons.Ticket />
					<Text>{booking.booking_confirmation_number}</Text>
				</Inline>
			) : null}
		</Stack>
	)
}

function OtaBookingHotel({ booking }: { booking: TOtaBooking }) {
	const { hotel } = booking
	return (
		<Stack gap="1" style={{ minWidth: "10rem" }}>
			<Text>{hotel.name}</Text>
			{hotel.location.name ? (
				<Inline gap="1" alignItems="center" color="muted" fontSize="sm">
					<Icons.LocationMarker />
					<Text>{hotel.location.name}</Text>
				</Inline>
			) : null}
		</Stack>
	)
}

function OtaBookingGuest({ booking }: { booking: TOtaBooking }) {
	const { latest_booking, trip_source, trip, is_b2b } = booking
	return (
		<Stack gap="1">
			<Text fontWeight="semibold">
				{is_b2b && trip_source ? trip_source.name : latest_booking.guest_name}
			</Text>
			{trip || !is_b2b ? (
				<Inline gap="1" alignItems="center" color="muted" fontSize="sm">
					{trip ? (
						<>
							<Text>Trip</Text>
							<Link
								to={generatePath("/trips/:tripId", { tripId: String(trip.id) })}
								color="accent"
							>
								#{trip.id}
							</Link>
						</>
					) : null}
					{is_b2b ? null : (
						<Badge primary borderWidth="0">
							Guest
						</Badge>
					)}
				</Inline>
			) : null}
		</Stack>
	)
}

function OtaBookingStayDates({ booking }: { booking: TOtaBooking }) {
	const { latest_booking } = booking
	const checkIn = localOrUtcTimestampToLocalDate(
		latest_booking.checkin_date_local,
		latest_booking.checkin_date
	)
	const checkOut = localOrUtcTimestampToLocalDate(
		latest_booking.checkout_date_local,
		latest_booking.checkout_date
	)
	const isSameMonth =
		formatDate(checkIn, "MMM YYYY") === formatDate(checkOut, "MMM YYYY")
	return (
		<Stack gap="1">
			<Inline gap="1" alignItems="center">
				<Icons.CalendarDays />
				<Text whiteSpace="nowrap">
					{isSameMonth
						? `${formatDate(checkIn, "D")} - ${formatDate(checkOut, "D MMM YYYY")}`
						: `${formatDate(checkIn, "D MMM YYYY")} - ${formatDate(checkOut, "D MMM YYYY")}`}
				</Text>
			</Inline>
			<Inline gap="1" alignItems="center" color="muted">
				<Icons.Moon />
				<Text>{getDiff(checkOut, checkIn, "days")}N</Text>
			</Inline>
		</Stack>
	)
}

function OtaBookingOccupancy({ booking }: { booking: TOtaBooking }) {
	const { no_of_adults, children } = booking.latest_booking
	return (
		<Inline gap="3" alignItems="center">
			<Inline gap="1" alignItems="center">
				<Icons.Users /> {no_of_adults}
			</Inline>
			<OtaBookingChildrenCount guestChildren={children} />
		</Inline>
	)
}

function childrenAgesOf(children: Array<TOtaBookingChild>) {
	return children
		.flatMap((child) => Array<number>(child.count).fill(child.age))
		.sort((a, b) => a - b)
}

export function OtaBookingChildrenCount({
	guestChildren,
	iconColor,
	...props
}: {
	guestChildren: Array<TOtaBookingChild>
	iconColor?: ComponentProps<typeof Icons.Child>["color"]
} & Pick<ComponentProps<typeof Inline>, "fontWeight" | "fontSize">) {
	const childrenAges = childrenAgesOf(guestChildren)
	if (!childrenAges.length) return null
	return (
		<Tooltip content={childrenAges.map((age) => `${age}y`).join(", ")}>
			<Inline gap="1" alignItems="center" {...props}>
				<Icons.Child color={iconColor} /> {childrenAges.length}
			</Inline>
		</Tooltip>
	)
}

function OtaBookingStageCell({
	booking,
	canRetry,
	onChange,
}: {
	booking: TOtaBooking
	canRetry: boolean
	onChange: () => void
}) {
	const { latest_stage, remote_booking_is_synced } = booking
	return (
		<Stack gap="1">
			<Inline gap="2" alignItems="center">
				<Badge
					status={latest_stage.state === "booked" ? "success" : undefined}
					borderWidth="0"
				>
					{latest_stage.state === "booked" ? "Booked" : "Cancelled"}
				</Badge>
				<OtaBookingSyncStatus booking={booking} />
				{!remote_booking_is_synced && canRetry ? (
					<RetryOtaBookingSync booking={booking} onChange={onChange} />
				) : null}
			</Inline>
			<Text color="muted" fontSize="sm">
				<Time timestamp={latest_stage.created_at} format={otaDateTimeFormat} />
			</Text>
		</Stack>
	)
}

// The ✓ or (!) of the last push to the hotel, with what happened on hover
function OtaBookingSyncStatus({ booking }: { booking: TOtaBooking }) {
	const {
		latest_stage,
		latest_booking,
		remote_booking_is_synced,
		remote_booking_synced_at,
		remote_booking_sync_attempted_at,
		remote_synced_booking,
	} = booking
	const isBooked = latest_stage.state === "booked"
	if (remote_booking_is_synced) {
		return (
			<Tooltip
				content={
					remote_booking_synced_at ? (
						<Text>
							{isBooked ? "Booking" : "Cancellation"} pushed to hotel on{" "}
							<Time
								timestamp={remote_booking_synced_at}
								format={otaDateTimeFormat}
							/>
						</Text>
					) : null
				}
			>
				<Box color="success" display="inlineFlex" alignItems="center">
					<Icons.Ok />
				</Box>
			</Tooltip>
		)
	}
	return (
		<Tooltip
			content={
				<Inline gap="1">
					<Icons.CircleAlert color="danger" />
					<Stack gap="1">
						<Inline gap="2" alignItems="center">
							<Text>
								Failed to push {isBooked ? "booking" : "cancellation"} to hotel
							</Text>
						</Inline>
						{remote_booking_sync_attempted_at ? (
							<Text>
								Last attempt at:{" "}
								<Time
									timestamp={remote_booking_sync_attempted_at}
									format={otaDateTimeFormat}
								/>{" "}
								• v{latest_booking.version}
							</Text>
						) : null}
						<Divider marginY="1" />
						<Text>
							{remote_synced_booking
								? `Current booking status at hotel - v${remote_synced_booking.version}`
								: "Booking never sent to Hotel"}
						</Text>
					</Stack>
				</Inline>
			}
		>
			<Box color="danger" display="inlineFlex" alignItems="center">
				<Icons.CircleAlert />
			</Box>
		</Tooltip>
	)
}

export function RetryOtaBookingSync({
	booking,
	onChange,
}: {
	booking: TOtaBooking
	onChange: () => void
}) {
	const [isLoading, setIsLoading] = useState(false)
	const pushed =
		booking.latest_stage.state === "booked" ? "Booking" : "Cancellation"
	return (
		<Batch
			onFinish={(batch) => {
				if (batch.failedJobs > 0 || batch.cancelledAt) {
					showSnackbar(`Failed to push ${pushed.toLowerCase()} to hotel.`)
				} else {
					showSnackbar(`${pushed} pushed to hotel.`)
				}
				// refresh either way; a partial failure may still change the row
				onChange()
			}}
		>
			{({ init, xhr, inProgress }) => {
				// The push is claimed before the request returns, so a refetch shows it under way
				const isInProgress = inProgress || booking.remote_booking_in_progress
				return (
					<Button
						level="primary"
						size="sm"
						disabled={isLoading || isInProgress}
						onClick={async () => {
							setIsLoading(true)
							try {
								const { message, data } = await hotelOtaBookingsXHR(
									xhr
								).syncOtaBookingToRemote(booking.id)
								showSnackbar(message)
								init(data)
							} catch (e) {
								// A 409 says a push is already under way
								showSnackbar(
									(e as Error).message ||
										"Something went wrong. Please try later."
								)
							} finally {
								setIsLoading(false)
								onChange()
							}
						}}
					>
						<Icons.Refresh
							animation={isLoading || isInProgress ? "spin" : undefined}
						/>{" "}
						{isLoading ? "Sending…" : isInProgress ? "In Progress..." : "Retry"}
					</Button>
				)
			}}
		</Batch>
	)
}

function OtaBookingPrice({ booking }: { booking: TOtaBooking }) {
	const { total_amount_money, paid_amount_money, due_amount_money } = booking
	return (
		<Inline gap="6" alignItems="center" justifyContent="end">
			<Stack gap="1" alignItems="end" fontWeight="semibold">
				<Text whiteSpace="nowrap">
					<Text as="span">Total: </Text>
					<Money money={total_amount_money} showCurrency />
				</Text>
				{Number(paid_amount_money.amount) ? (
					<Text color="success" whiteSpace="nowrap" fontSize="sm">
						Paid: <Money money={paid_amount_money} showCurrency />
					</Text>
				) : null}
				{Number(due_amount_money.amount) ? (
					<Text color="muted" whiteSpace="nowrap" fontSize="sm">
						Due: <Money money={due_amount_money} showCurrency />
					</Text>
				) : null}
			</Stack>
		</Inline>
	)
}
